import { HTMLProps } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { Separator } from '@givebutter/primitives/separator';
import { useToast } from '@givebutter/primitives/toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CustomFieldTypeFriendly } from './types';
import { createCustomField, fetchCustomField, updateCustomField } from 'api/customFields';
import * as CustomFieldForm from 'components/custom-fields';
import {
    customFieldTypeToReadable,
    toFriendlyType,
    typeIcon,
} from 'components/custom-fields/helpers';
import * as Dialog from 'components/dialog';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useInvalidateUserAndAccount } from 'customHooks/useInvalidateUserAndAccount';
import { conditionallyFetchOrNull } from 'helpers/reactQuery';
import { camelCase } from 'lodash';
import { customFieldQueryKeys } from 'queries/custom-field-query-keys';
import { twJoin } from 'tailwind-merge';
import { CustomField, CustomFieldGroup } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';
import { CustomFieldType } from 'types/filter';

interface BaseCustomFieldForm {
    title: string;
    description: string;
    type: CustomFieldType;
    group: CustomFieldGroup;
    answers: Array<{ value: string }> | null;
    defaultResponse?: string | boolean;
    showCreate: boolean;
    showCreateCompany: boolean;
}
interface CustomFieldFormRequest {
    title: string;
    description: string | null;
    type: CustomFieldType;
    group: CustomFieldGroup;
    answers: string[] | null;
    default_value: string | null;
    show_create: boolean;
    show_create_company: boolean;
}

interface TypeListProps extends HTMLProps<HTMLLIElement> {
    type: CustomFieldType;
}

const CustomFieldFormDialog = () => {
    const toast = useToast();
    const history = useHistory();
    const location = useLocation();
    const queryClient = useQueryClient();
    const { accountId, hasGivebutterPlusAccess } = useCurrentAccount();
    const { mutate: invalidateUserData } = useInvalidateUserAndAccount();

    const { custom_field_id } = useParams<{ custom_field_id?: string }>();
    const customFieldId = custom_field_id ? parseInt(custom_field_id) : -1;
    const hasCustomFieldId = customFieldId !== -1;
    const showFileOption = hasGivebutterPlusAccess;

    const { data: customField } = useSuspenseQuery({
        queryKey: customFieldQueryKeys.customFieldById(accountId, customFieldId),
        queryFn: () =>
            conditionallyFetchOrNull(
                () => fetchCustomField(accountId, customFieldId),
                hasCustomFieldId,
            ),
    });

    const form = toForm(customField);

    const methods = Form.useGivebutterForm<BaseCustomFieldForm>({
        defaultValues: form,
    });

    const {
        handleSubmit,
        setValue,
        watch,
        reset,
        setError,
        formState: { errors },
        resetFormButton,
        getValues,
    } = methods;

    const changeActiveType = (type: CustomFieldType) => {
        setValue('defaultResponse', defaultResponse(type));
        setValue('type', type);

        reset({
            ...getValues(),
            type,
            answers: type === 'select' ? [{ value: '' }] : null,
        });
    };

    const { mutate: submit } = useMutation({
        mutationFn: (transformed: CustomFieldFormRequest) => {
            return hasCustomFieldId
                ? updateCustomField(accountId, customFieldId, transformed)
                : createCustomField(accountId, transformed);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: customFieldQueryKeys.customFields(accountId),
            });
            invalidateUserData();
            toast.success(`Your custom field was ${hasCustomFieldId ? 'updated' : 'created'}.`);
            history.push(
                hasCustomFieldId
                    ? location.pathname.split('/custom-fields/')[0] + '/custom-fields'
                    : location.pathname.split('/new')[0],
            );
        },
        onError: (e: GivebutterAxiosError) => {
            const errors = e.response?.data?.errors;

            for (let k in errors) {
                let key = camelCase(k) as keyof BaseCustomFieldForm;
                setError(key, { type: 'custom', message: errors[k][0] });
            }

            setError('root', {
                type: 'custom',
                message: `Could not ${hasCustomFieldId ? 'update' : 'create'} the custom field.`,
            });

            resetFormButton();
        },
    });

    const watchType = watch('type');
    const activeType = toFriendlyType(watchType);

    const onSubmit: SubmitHandler<BaseCustomFieldForm> = data => {
        submit(toRequest(data));
    };

    const customFieldClass = getActiveCustomFieldClass(activeType);

    return (
        <Form.Provider {...methods}>
            <Dialog.Body className={'flex flex-col gap-4'}>
                <Dialog.Title>{hasCustomFieldId ? 'Edit' : 'New'} Custom Field</Dialog.Title>
                <Dialog.Close />
                <Separator className="-mx-6 !w-auto" />
                <div className={twJoin('flex gap-4')}>
                    {!hasCustomFieldId ? (
                        <>
                            <aside className={'-mx-2 w-48'}>
                                <ul className={'flex flex-col gap-y-1'}>
                                    <TypeListItem
                                        type={'text'}
                                        onClick={() => changeActiveType('text')}
                                        className={customFieldClass('text')}
                                    />
                                    <TypeListItem
                                        type={'number'}
                                        onClick={() => changeActiveType('number')}
                                        className={customFieldClass('number')}
                                    />

                                    <TypeListItem
                                        type={'daterange'}
                                        onClick={() => changeActiveType('date')}
                                        className={customFieldClass('date')}
                                    />

                                    <TypeListItem
                                        type={'checkbox'}
                                        onClick={() => changeActiveType('checkbox')}
                                        className={customFieldClass('checkbox')}
                                    />

                                    <TypeListItem
                                        type={'select'}
                                        onClick={() => changeActiveType('select')}
                                        className={customFieldClass('option')}
                                    />

                                    {showFileOption ? (
                                        <TypeListItem
                                            type={'file'}
                                            onClick={() => changeActiveType('file')}
                                            className={customFieldClass('file')}
                                        />
                                    ) : null}
                                </ul>
                            </aside>
                            <Separator className={'-my-4 !h-auto'} orientation={'vertical'} />
                        </>
                    ) : null}

                    <main className={`flex max-h-[80vh] w-full flex-col gap-y-8 overflow-y-auto`}>
                        {errors.root ? (
                            <div className={'flex justify-center rounded-lg bg-red-50 p-2'}>
                                <Form.Error name={'root'} />
                            </div>
                        ) : null}
                        <form className={'contents'} onSubmit={handleSubmit(onSubmit)}>
                            <CustomFieldForm.Root customField={customField}>
                                <CustomFieldForm.Title />
                                <CustomFieldForm.Description>
                                    <CustomFieldForm.FieldDescription>
                                        Shows beneath input to aid team members.
                                    </CustomFieldForm.FieldDescription>
                                </CustomFieldForm.Description>
                                <CustomFieldForm.Type />
                                <CustomFieldForm.ShowCreate />
                                <Form.Container className={'!flex-row !justify-between'}>
                                    <div>
                                        <CustomFieldForm.ArchiveButton />
                                    </div>
                                    <div className={'flex gap-x-3'}>
                                        <Dialog.Cancel asChild>
                                            <Button
                                                color={'neutral'}
                                                variant={'outline'}
                                                className={'!w-auto'}
                                            >
                                                Cancel
                                            </Button>
                                        </Dialog.Cancel>
                                        <Form.SubmitButton className={'transition-all'}>
                                            {hasCustomFieldId ? 'Save Edits' : 'Create'}
                                        </Form.SubmitButton>
                                    </div>
                                </Form.Container>
                            </CustomFieldForm.Root>
                        </form>{' '}
                    </main>
                </div>
            </Dialog.Body>
        </Form.Provider>
    );
};

const TypeListItem = ({ type, className, ...props }: TypeListProps) => {
    return (
        <li
            className={`flex cursor-pointer items-baseline gap-x-1 rounded-lg p-2 font-medium ${className}`}
            {...props}
        >
            <div className={'w-5'}>
                <FontAwesomeIcon icon={typeIcon(type)} />
            </div>
            <span>{customFieldTypeToReadable(type)}</span>
        </li>
    );
};

const toForm = (request: CustomField | null) => ({
    id: request?.id,
    title: request?.title || '',
    description: request?.description || '',
    type: request?.type || 'text',
    group: request?.group || 'contact',
    answers: request?.answers ? CustomFieldForm.toFieldArray(request.answers) : null,
    defaultResponse: defaultResponse(request?.type ?? 'text', request?.default_value),
    showCreate: request?.metadata?.show_create ?? true,
    showCreateCompany: request?.metadata?.show_create_company ?? true,
});

const defaultResponse = (
    type: CustomFieldType,
    defaultValue?: CustomFieldFormRequest['default_value'],
) => {
    switch (type) {
        case 'text':
        case 'date':
        case 'select':
            return undefined;
        case 'checkbox':
            return defaultValue ?? false;
        case 'percentage':
        case 'number':
            return defaultValue ?? '';
        default:
            return '';
    }
};

const toRequest = (formValues: BaseCustomFieldForm): CustomFieldFormRequest => ({
    title: formValues.title,
    description: formValues.description || null,
    type: formValues.type,
    group: formValues.group,
    answers:
        CustomFieldForm.typeHasAnswers(formValues.type) &&
        formValues.answers &&
        CustomFieldForm.toFlatArray(formValues.answers).length
            ? CustomFieldForm.toFlatArray(formValues.answers)
            : null,
    default_value: defaultValue(formValues.type, formValues.defaultResponse),
    show_create: formValues.showCreate,
    show_create_company: formValues.showCreateCompany,
});

const defaultValue = (
    type: CustomFieldType,
    defaultResponse: BaseCustomFieldForm['defaultResponse'],
) => {
    switch (type) {
        case 'checkbox':
            return defaultResponse ? '1' : null;
        case 'percentage':
        case 'number':
            return defaultResponse as string;
        default:
            return null;
    }
};

const getActiveCustomFieldClass =
    (activeType: CustomFieldTypeFriendly) => (type: CustomFieldTypeFriendly) =>
        activeType === type ? 'rounded-lg bg-blue-50 text-blue-600' : '';

export type { BaseCustomFieldForm };
export { CustomFieldFormDialog };
